import { FilterValues } from 'src/model';
import { EmployeesRangeValues, MinMaxOptionsValues } from 'src/utils/filterOptions';
import { FiltersArray } from 'src/utils/apollofilterObjContacts';
import { CountryOptions } from 'src/constants/countryFilterOptions';
import { SeniorityDesignation } from './ICPSubDesignationValues';
import { employeeByDeptESHashV2 } from 'src/features/multiDbExternal/utils/constants';

export const generateFilterValues = (filterObj: FiltersArray, uniqueValues: string) => {
  switch (filterObj.type) {
    case 'enrichment-validation':
    case 'enrichment-radio-group':
      return JSON.parse(uniqueValues || `[]`) || [];
    case 'multi-select':
      return generateSelectDropdownValues(uniqueValues);
    case 'radio-button-group':
    case 'radio-button-group-array':
    case 'checkbox-button-group':
    case 'company-headcount':
    case 'department-headcount':
      return generateRadioGroupOptions(uniqueValues);
    case 'country-multi-select':
      return CountryOptions;

    case 'single-select':
      return generateSelectDropdownValues(uniqueValues);

    case 'toggle-filter':
      return [];
    case 'date-range':
      return [];

    case 'min-max':
      if (filterObj.key.includes('employee')) {
        return generateMinMaxValues('employee');
      } else if (
        filterObj.key.toLowerCase().includes('revenue') ||
        filterObj.key.toLowerCase().includes('amount') ||
        filterObj?.category === 'Amount'
      ) {
        return generateMinMaxValues('revenue');
      } else if (filterObj.key.toLowerCase().includes('year')) {
        return generateMinMaxValues('year');
      } else {
        return generateMinMaxValues('default');
      }

    case 'ICP-designation':
      return SeniorityDesignation;

    case 'nested-checkbox':
      return JSON.parse(uniqueValues);

    case 'preload-nested-checkbox':
      return [];

    case 'webpages-funnel':
      return [];

    case 'async-multi-select':
      return [];

    case 'preload-async-multiselect':
      return [];

    case 'tal-dept':
    case 'tal-industry':
    case 'tal-designation':
    case 'icp-fitment-filter':
      return [];

    case 'emp-by-dept':
      return generateSelectDropdownValues(uniqueValues);
    case 'employeeByDepartment':
      return generateTalEmployeeByDepartment(uniqueValues);
    case 'nested-checkbox-with-search':
      return [];
    case 'engagement-score-filter':
      return [];
    case 'country-presence-filter':
      return [];
    default:
      break;
  }
};

export const generateRadioGroupOptions = (uniqueValues: string) => {
  const radioOptions: FilterValues = [];
  const options = JSON.parse(uniqueValues) || [];
  for (let i = 0; i < options.length; i++) {
    radioOptions.push({
      label: options[i]?.label ? options[i]?.label : options[i],
      id: options[i]?.id ? options[i]?.id : options[i],
    });
  }
  return radioOptions;
};

export const generateSelectDropdownValues = (uniqueValues: string) => {
  const multiSelectValues: FilterValues = [];
  const values = JSON.parse(uniqueValues) || [];
  for (let i = 0; i < values.length; i++) {
    multiSelectValues.push({
      label: values[i] ? values[i] : 'Unavailable',
      id: values[i],
    });
  }
  return multiSelectValues;
};

export const generateMinMaxValues = (type: string) => {
  switch (type) {
    case 'revenue':
      return MinMaxOptionsValues;

    case 'employee':
      return EmployeesRangeValues;

    case 'year':
      return generateYearValues();

    default:
      return EmployeesRangeValues;
  }
};

export const generateYearValues = () => {
  const years = [];
  const currentYear = new Date().getFullYear();
  const yearsToDisplay = 1000;

  for (let i = currentYear; i >= currentYear - yearsToDisplay; i--) {
    years.push({ label: i.toString(), id: i });
  }
  return years;
};

export function transformFilters(input: any[]): any[] {
  const filterGroups: { [key: string]: any } = {};

  for (const filter of input) {
    if (!filterGroups[filter.filterGroup]) {
      filterGroups[filter.filterGroup] = {
        label: filter.filterGroup,
        key: filter.filterGroup,
        category: filter?.category || null,
        type: '',
        fetchUrl: filter?.fetchURL || '',
        labelKey: filter?.labelKey || '',
        valueKey: filter?.valueKey || '',
        scope: [],
        fetch: false,
        filterData: {
          childFilters: [],
        },
      };
    }

    const childFilter: any = {
      label: filter?.FilterName || '',
      key: filter?.FilterName || '',
      type:
        filter?.FilterName === 'Global Presence'
          ? 'country-presence-filter'
          : filter?.filterType || '',
      fetchUrl: filter?.fetchUrl || filter?.fetchURL || '',
      category: filter?.category || null,
      labelKey: filter?.labelKey || '',
      valueKey: filter?.valueKey || '',
      scope: filter?.scope || (filter?.filterType === 'min-max' ? [] : []),
      fetch: false,
      filterData: {
        filterValues: {},
        ...(filter?.filterType === 'ai-insights-filter' && {
          uniqueValues: filter?.UniqueValues || [],
        }),
        ...(filter?.filterType === 'ask-ai-filter' && {
          uniqueValues: filter?.UniqueValues || [],
        }),
      },
    };

    childFilter.filterData.filterValues = generateFilterValues(
      childFilter,
      JSON.stringify(filter.UniqueValues),
    );

    filterGroups[filter.filterGroup].filterData.childFilters.push(childFilter);
  }

  return Object.values(filterGroups);
}

export function createFilterTypeMap(filtersInfo: any[]): Map<string, string> {
  const filterTypeMap = new Map<string, string>();
  if (!filtersInfo || !Array.isArray(filtersInfo)) return filterTypeMap;
  for (const info of filtersInfo) {
    filterTypeMap.set(info.FilterName, info.filterType);
  }
  return filterTypeMap;
}

export function addFilterTypeToSelectedFilters(
  selectedFilters: any[],
  filterTypeMap: Map<string, string>,
): any[] {
  for (const selectedFilter of selectedFilters) {
    const filterType = filterTypeMap.get(selectedFilter.filterName);
    if (filterType) {
      selectedFilter.filterType = filterType;
    }
    if (selectedFilter.filterName === 'Headcount Growth') {
      selectedFilter.filterType = 'company-headcount';
    }
    if (selectedFilter.filterName === 'contactName') {
      selectedFilter.filterName = 'Contact Name';
      selectedFilter.filterType = 'contactName';
    }
    if (selectedFilter.filterName === 'accountName') {
      selectedFilter.filterName = 'Account Name';
      selectedFilter.filterType = 'accountName';
    }
    if (selectedFilter.filterName === 'Saas / Non-Saas') {
      selectedFilter.filterType = 'checkbox-button-group';
    }
    if (selectedFilter.filterName === 'Similar Companies') {
      selectedFilter.filterType = 'checkbox-button-group';
    }
    if (selectedFilter.filterName === 'Confidence Scores') {
      selectedFilter.filterType = 'checkbox-button-group';
    }
    if (selectedFilter.filterName === 'Traffic Source') {
      selectedFilter.filterType = 'radio-button-group';
    }
  }
  return selectedFilters;
}

function isNotEmpty(value: any): boolean {
  return (
    value !== null &&
    value !== undefined &&
    (typeof value !== 'object' || Object.keys(value).length > 0)
  );
}

export function removeEmptyElements(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return isNotEmpty(obj) ? obj : null;
  }

  if (Array.isArray(obj)) {
    const filteredArray = obj
      .map((item) => {
        return removeEmptyElements(item);
      })
      .filter(isNotEmpty);

    return filteredArray.length > 0 ? filteredArray : null;
  }

  const newObj: { [key: string]: any } = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = removeEmptyElements(obj[key]);
      if (isNotEmpty(value)) {
        newObj[key] = value;
      }
    }
  }
  return Object.keys(newObj).length > 0 ? newObj : null;
}

export const filterSequenceList = [
  'List',
  'List Name',
  'Company',
  'Companies',
  'Organization',
  'Location',
  'Account Location',
  'Contact Location',
  'Company Region',
  'Job Title',
  'Department',
  'Designation',
  'Seniority',
  'Industry',
  'Company keyword',
  'Employee Count',
  'Employee by dept',
  'Buying Intent',
  'Technology',
  'Revenue',
  'Funding',
  'CRM',
  'Founded year',
  'Contact Owner',
  'Account Owner',
  'Verified Email',
  'Email Status',
  'Source',
  'Source File',
  'Website Visits',
  'Channel',
  'Visiting location',
  'Qualification',
  'Owner',
  'Job Openings',
  'Account ICP',
  'Connection Path',
  'Event',
  'AI Insights',
  'SalesNav Insights',
  'Miller Heiman Role',
  'Custom Field',
];

export function sortFiltersBySequence(inputArray: any, sequenceArray: string[]) {
  // Create a map of labels to their respective positions in the sequence
  const sequenceMap = new Map();
  sequenceArray.forEach((label, index) => {
    sequenceMap.set(label.toLowerCase(), index);
  });

  // Define a custom sorting function
  function customSort(a: any, b: any) {
    const labelA = a.label.toLowerCase();
    const labelB = b.label.toLowerCase();
    const indexA = sequenceMap.get(labelA);
    const indexB = sequenceMap.get(labelB);

    // If both labels are not in the sequence, maintain their relative order in the input array
    if (indexA === undefined && indexB === undefined) {
      return inputArray.indexOf(a) - inputArray.indexOf(b);
    }

    // If labelA is not in the sequence, move it to the end
    if (indexA === undefined) {
      return 1;
    }

    // If labelB is not in the sequence, move it to the end
    if (indexB === undefined) {
      return -1;
    }

    // Sort based on the position in the sequence
    return indexA - indexB;
  }

  // Sort the inputArray using the customSort function
  return inputArray.slice().sort(customSort);
}

const generateTalEmployeeByDepartment = (uniqueValues: any) => {
  const values = JSON.parse(uniqueValues) || [];

  return values.map((dept: any) => ({
    label: employeeByDeptESHashV2[dept?.id] || dept.name,
    id: dept.id,
  }));
};
